import { create } from "zustand"
import { db } from "@/lib/firebase"
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  orderBy,
} from "firebase/firestore"
import { useAuthStore } from "@/store/authStore"
import { toast } from "@/hooks/use-toast"

export interface PersonalReminder {
  id: string
  userId: string
  date: string          // "yyyy-MM-dd"
  title: string
  time?: string         // "HH:mm"
  description?: string
  createdAt: any
  updatedAt: any
}

interface PersonalRemindersState {
  reminders: PersonalReminder[]
  loading: boolean
  init: () => void
  addReminder: (date: string, title: string, time?: string, description?: string) => Promise<void>
  updateReminder: (id: string, title: string, time?: string, description?: string) => Promise<void>
  deleteReminder: (id: string) => Promise<void>
  getRemindersForDate: (date: string) => PersonalReminder[]
  cleanup: () => void
}

let unsubscribe: (() => void) | null = null

export const usePersonalRemindersStore = create<PersonalRemindersState>((set, get) => ({
  reminders: [],
  loading: false,

  init: () => {
    const user = useAuthStore.getState().user
    if (!user) {
      console.warn("⚠️ Пользователь не авторизован — напоминания не загружаются")
      return
    }

    set({ loading: true })

    // Отписываемся от предыдущей подписки, если есть
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }

    const q = query(
      collection(db, "personalReminders"),
      where("userId", "==", user.id),
      orderBy("date", "asc")
    )

    console.log(`🔄 Подписка на reminders для userId: ${user.id}`)

    unsubscribe = onSnapshot(
      q,
      (snap) => {
        const loaded = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as PersonalReminder[]

        set({ reminders: loaded, loading: false })
        console.log(`✅ Загружено личных напоминаний: ${loaded.length}`)
      },
      (err: any) => {
        console.error("❌ Reminders error:", err)
        
        if (err.code === "failed-precondition" && err.message.includes("index")) {
          toast({
            title: "Требуется индекс Firestore",
            description: "Перейди по ссылке из консоли браузера и создай индекс",
            variant: "destructive",
            duration: 15000,
          })
        } else {
          toast({ title: "Ошибка загрузки напоминаний", variant: "destructive" })
        }
        
        set({ loading: false })
      }
    )
  },

  addReminder: async (date: string, title: string, time?: string, description?: string) => {
    const user = useAuthStore.getState().user
    if (!user || !title?.trim()) {
      toast({ title: "Заголовок напоминания обязателен", variant: "destructive" })
      return
    }

    try {
      const newReminder = {
        userId: user.id,
        date,
        title: title.trim(),
        time: time?.trim() || undefined,
        description: description?.trim() || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }

      await addDoc(collection(db, "personalReminders"), newReminder)

      toast({
        title: "Напоминание сохранено ✅",
        description: time ? `${title} — ${time}` : title,
      })
    } catch (err) {
      console.error("Ошибка добавления напоминания:", err)
      toast({ title: "Не удалось сохранить напоминание", variant: "destructive" })
    }
  },

  updateReminder: async (id: string, title: string, time?: string, description?: string) => {
    try {
      await updateDoc(doc(db, "personalReminders", id), {
        title: title.trim(),
        time: time?.trim() || null,
        description: description?.trim() || "",
        updatedAt: serverTimestamp(),
      })
      toast({ title: "Напоминание обновлено" })
    } catch (err) {
      console.error("Ошибка обновления:", err)
      toast({ title: "Не удалось обновить напоминание", variant: "destructive" })
    }
  },

  deleteReminder: async (id: string) => {
    try {
      await deleteDoc(doc(db, "personalReminders", id))
      toast({ title: "Напоминание удалено 🗑️" })
    } catch (err) {
      console.error("Ошибка удаления:", err)
      toast({ title: "Не удалось удалить напоминание", variant: "destructive" })
    }
  },

  getRemindersForDate: (date: string) => {
    return get().reminders.filter((r) => r.date === date)
  },

  cleanup: () => {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    set({ reminders: [] })
  },
}))